import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, UserCheck, Leaf, ScanSearch, X } from 'lucide-react';
import { useContent } from '../../lib/content';
import { pickLocale } from '../../lib/api';
import { ENFORCEMENT_DATE, BANNER_DISMISSED_KEY } from '../../lib/compliance';

/**
 * RegulatorBanner — sticky bottom banner that ROTATES across the 4 MODARIA
 * moats every 6s. Discreet, dismissible, persists dismissal in localStorage.
 * Visible on every breakpoint with a compact mobile layout.
 */

const MOATS = [
  {
    key: 'compliance',
    icon: Shield,
    target: '#compliance',
    es: (days) => `Ley Europea de IA · Art. 50 · ${days}d para entrar en vigor`,
    en: (days) => `EU AI Act · Art. 50 · ${days}d to enforcement`,
    cta_es: 'Examina tu proceso',
    cta_en: 'Audit your process',
  },
  {
    key: 'likeness',
    icon: UserCheck,
    target: '#likeness',
    es: () => 'Caja Fuerte de Permisos · derechos de imagen firmados por activo',
    en: () => 'Vault of Permissions · signed image rights per asset',
    cta_es: 'Cubre derechos',
    cta_en: 'Clear rights',
  },
  {
    key: 'impact',
    icon: Leaf,
    target: '#impact',
    es: () => 'Libro de Impacto · CO₂, agua y residuos auditados',
    en: () => 'Impact Book · audited CO₂, water and waste',
    cta_es: 'Reduce huella',
    cta_en: 'Cut footprint',
  },
  {
    key: 'forensics',
    icon: ScanSearch,
    target: '#contact',
    es: () => 'Verificación de Origen · audita el catálogo que ya tienes',
    en: () => 'Origin Verification · audit the catalogue you already have',
    cta_es: 'Pídelo en la demo',
    cta_en: 'Ask in demo',
  },
];

const ROTATION_MS = 6000;

export default function RegulatorBanner() {
  const { locale } = useContent();
  const [visible, setVisible] = useState(false);
  const [days, setDays] = useState(0);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const dismissed = window.localStorage.getItem(BANNER_DISMISSED_KEY) === '1';
    setVisible(!dismissed);

    const tick = () => {
      const ms = ENFORCEMENT_DATE.getTime() - Date.now();
      setDays(Math.max(0, Math.floor(ms / 86_400_000)));
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % MOATS.length), ROTATION_MS);
    return () => clearInterval(id);
  }, [paused]);

  const dismiss = () => {
    setVisible(false);
    if (typeof window !== 'undefined') window.localStorage.setItem(BANNER_DISMISSED_KEY, '1');
  };

  const moat = MOATS[idx];
  const Icon = moat.icon;
  const labelLong = locale === 'es' ? moat.es(days) : moat.en(days);
  const labelShort = locale === 'es'
    ? (moat.key === 'compliance' ? `AI ACT · ${days}D` : moat.key.toUpperCase())
    : (moat.key === 'compliance' ? `AI ACT · ${days}D` : moat.key.toUpperCase());
  const cta = locale === 'es' ? moat.cta_es : moat.cta_en;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
          className="fixed bottom-3 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 md:gap-3 bg-bg/85 backdrop-blur-md border border-line-strong px-3 md:px-4 py-2 md:py-2.5 shadow-[0_8px_24px_rgba(0,0,0,0.35)] max-w-[calc(100vw-1.5rem)]"
          data-testid="regulator-banner"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Dot indicator strip */}
          <div className="hidden md:flex items-center gap-1 mr-1" data-testid="regulator-banner-dots">
            {MOATS.map((m, i) => (
              <button
                key={m.key}
                onClick={() => setIdx(i)}
                aria-label={m.key}
                data-testid={`regulator-banner-dot-${m.key}`}
                className={`block h-px transition-all duration-500 ${i === idx ? 'w-5 bg-acid' : 'w-2.5 bg-ink-dim hover:bg-ink-muted'}`}
              />
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={moat.key}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35 }}
              className="flex items-center gap-2 md:gap-3"
              data-testid={`regulator-banner-message-${moat.key}`}
            >
              <Icon size={13} className="text-acid shrink-0" />
              <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.2em] md:tracking-[0.22em] text-ink whitespace-nowrap">
                <span className="hidden sm:inline">{labelLong}</span>
                <span className="sm:hidden">{labelShort}</span>
              </span>
              <a
                href={moat.target}
                data-testid="regulator-banner-cta"
                className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.2em] md:tracking-[0.22em] text-acid hover:underline whitespace-nowrap"
              >
                {cta} →
              </a>
            </motion.div>
          </AnimatePresence>

          <button
            type="button"
            onClick={dismiss}
            aria-label={locale === 'es' ? 'Cerrar aviso' : 'Dismiss'}
            data-testid="regulator-banner-close"
            className="text-ink-muted hover:text-ink transition-colors ml-0.5 md:ml-1"
          >
            <X size={12} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
